/**
 * The cosmetic-award "level-up" sound cue (PRD #101 / ADR-0048). Unlike footsteps
 * and doors - positional sounds the {@link SoundObserver} derives by diffing world
 * snapshots - the level-up sound is a one-shot UI reward cue tied to a single
 * discrete event: the server's private, self-only "New Cosmetic Award!" emote, the
 * one source of truth for "I just crossed a 5-minute unlock boundary and got a new
 * cosmetic". That emote arrives as a {@link SpeechMessage} flagged `action: true`,
 * sent to the earner alone and only on a genuine fresh unlock (a reconnect, resync,
 * or saved-outfit login re-awards nothing, so no emote and no sound). Keeping the
 * decision here - pure, snapshot-free, and unit-tested - mirrors how the observer
 * isolates the *decision* from {@link AudioManager}'s *playback*.
 */
import type { SoundEvent } from './soundObserver';
import { isAwardEmote, type SpeechMessage } from '../net/protocol';

/**
 * The level-up cue plays at full volume: it is the local player's own reward, not a
 * sound in the world, so it is not proximity-scaled the way footsteps/doors are.
 */
const LEVEL_UP_VOLUME = 1;

/**
 * The {@link SoundEvent} to play for a cosmetic-award emote, or `null` for every
 * other line - ordinary speech (player chat, NPC barks, the `/top` herald) *and*
 * other broadcast action emotes (e.g. the dice roll, ADR-0083). The award emote is
 * identified through the shared {@link isAwardEmote} predicate, the one source of
 * truth every award-emote surface keys off (overhead, this cue, journal-suppression);
 * everything else is silent here. Playback still routes through
 * {@link AudioManager.play}, so the master mute (settings panel) governs it.
 */
export function awardCue(speech: SpeechMessage): SoundEvent | null {
  return isAwardEmote(speech) ? { sound: 'level-up', volume: LEVEL_UP_VOLUME } : null;
}
